import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Phone,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  CreditCard
} from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { useBooking } from '../../services/bookingService';
import { formatStatusText, getStatusColor } from '../../utils';
import BalancePaymentModal from '../../components/client/BalancePaymentModal';
import BookingReceipt from '../../components/client/BookingReceipt';
import PaymentHistoryModal from '../../components/client/PaymentHistoryModal';
import ReviewModal from '../../components/client/ReviewModal';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { paymentApi } from '../../services/paymentService';
import { Star } from 'lucide-react';
import EventLogs from '../../components/shared/EventLogs';
import { useEventLogs } from '../../hooks/useEventLogs';
import BookingChatPanel from '../../components/booking/BookingChatPanel';
import { bookingChatUnreadQueryKey, fetchBookingChatUnreadCount } from '../../services/bookingChatService';

const ClientBookingDetails = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: bookingResponse, isLoading, error } = useBooking(id);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showBookingChat, setShowBookingChat] = useState(false);
  
  const booking = bookingResponse?.data;

  const closeBookingChat = () => {
    setShowBookingChat(false);
    if (id) {
      queryClient.invalidateQueries({ queryKey: bookingChatUnreadQueryKey(id, false) });
    }
  };

  const { data: chatUnreadResponse } = useQuery({
    queryKey: bookingChatUnreadQueryKey(id, false),
    queryFn: () => fetchBookingChatUnreadCount(id, false),
    enabled: Boolean(id && booking && !showBookingChat),
    refetchInterval: showBookingChat ? false : 12000,
  });
  const chatUnreadCount = chatUnreadResponse?.data?.unread_count ?? 0;
  
  // Fetch event logs
  const { eventLogs, isLoading: isLoadingEventLogs } = useEventLogs(id);
  
  // Check if booking has a review
  const hasReview = booking?.reviews && booking.reviews.length > 0;
  const canReview = booking?.status === 'completed' && !hasReview;

  // Fetch payments for this booking
  const { data: paymentsResponse } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => paymentApi.getPaymentByBooking(id),
    enabled: !!id && !!booking,
    select: (response) => response?.data || []
  });

  const payments = paymentsResponse || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        // Try parsing as time string directly
        return timeString;
      }
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return timeString;
    }
  };

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <ClientPageLayout title="Booking Details">
      <div className="space-y-3 sm:space-y-2 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="h-5 w-32 bg-gray-200 rounded"></div>
          <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
        </div>

            {/* Booking Reference Skeleton */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-40 bg-gray-200 rounded"></div>
            </div>
            <div className="h-9 w-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          {/* Main Content Skeleton */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            {/* Event Details Skeleton */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <div className="h-5 w-32 bg-gray-200 rounded mb-3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="h-4 w-4 bg-gray-200 rounded mt-1"></div>
                    <div className="flex-1">
                      <div className="h-3 w-20 bg-gray-200 rounded mb-1"></div>
                      <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Service & Package Skeleton */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <div className="h-5 w-40 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 w-32 bg-gray-200 rounded mb-1"></div>
              <div className="h-4 w-full bg-gray-200 rounded"></div>
            </div>
          </div>

          {/* Sidebar Skeleton */}
          <div className="space-y-2 sm:space-y-3">
            {/* Vendor Information Skeleton */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <div className="h-5 w-40 bg-gray-200 rounded mb-3"></div>
              <div className="space-y-2">
                <div className="h-3 w-24 bg-gray-200 rounded mb-1"></div>
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-3 w-20 bg-gray-200 rounded mb-1"></div>
                <div className="h-4 w-28 bg-gray-200 rounded"></div>
              </div>
              <div className="mt-4 pt-3 border-t-2 border-gray-200 space-y-2">
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
              </div>
            </div>

            {/* Payment Summary Skeleton */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <div className="h-5 w-36 bg-gray-200 rounded mb-3"></div>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <div className="h-3 w-20 bg-gray-200 rounded"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t-2 border-gray-200 space-y-2">
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClientPageLayout>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error || !booking) {
    return (
      <ClientPageLayout title="Booking Details">
        <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-black mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">Booking Not Found</h3>
            <p className="text-gray-600 mb-6">
              {error?.response?.data?.message || 'The booking you are looking for does not exist.'}
            </p>
            <Link
              to="/bookings"
              className="inline-flex items-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Bookings
            </Link>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  const vendor = booking.vendor;
  const service = booking.vendor_service;
  const packageData = booking.vendor_service_package;

  // Helper function to safely parse and format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === null || num === undefined) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  // Calculate total due and balance using new structure
  const subtotal = parseFloat(booking?.subtotal_amount) || 0;
  const platformFee = parseFloat(booking?.platform_fee) || 0;
  const totalDue = subtotal + platformFee;
  const amountPaid = parseFloat(booking?.amount_paid) || 0; // Use booking.amount_paid from database
  
  // Calculate payment totals from actual payments
  const completedPayments = payments.filter(p => p.status === 'completed');
  const totalPaidFromPayments = completedPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  
  // Use amount_paid from booking if available, otherwise calculate from payments
  const actualAmountPaid = amountPaid > 0 ? amountPaid : totalPaidFromPayments;
  
  const depositPayments = completedPayments.filter(p => p.payment_type === 'deposit');
  const fullPayments = completedPayments.filter(p => p.payment_type === 'full_payment');
  const depositPaid = depositPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const fullPaymentPaid = fullPayments.length > 0;
  const fullPaymentAmount = fullPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  
  const balanceAmount = Math.max(0, totalDue - actualAmountPaid);
  const isFullyPaid = totalDue > 0 && actualAmountPaid >= totalDue;
  
  // Only show deposit paid if:
  // 1. There are actual deposit payments
  // 2. The booking is NOT fully paid (if fully paid, don't show deposit - it's a full payment)
  // 3. No full payment was made
  const showDepositPaid = depositPaid > 0 && !isFullyPaid && !fullPaymentPaid;

  const handlePaymentSuccess = () => {
    // Refresh booking and payment data
    queryClient.invalidateQueries(['booking', id]);
    queryClient.invalidateQueries(['payments', id]);
    setShowPaymentModal(false);
    // Optionally show success message or redirect
    window.location.reload(); // Simple refresh for now
  };

  const handleReviewSuccess = () => {
    // Refresh booking data to show the new review
    queryClient.invalidateQueries(['booking', id]);
  };


  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    // Error is already displayed in the StripePaymentForm component
  };

  return (
    <ClientPageLayout title="Booking Details">
      <div className="space-y-3 sm:space-y-2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <Link
            to="/bookings"
            className="inline-flex items-center text-black hover:text-gray-600 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bookings
          </Link>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(booking.status)}`}>
              {formatStatusText(booking.status)}
            </span>
          </div>
        </div>

        {/* Booking Reference */}
        <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-black mb-0.5">
                Booking #{booking.booking_reference}
              </h2>
              <p className="text-xs text-gray-600">
                Created on {formatDate(booking.created_at)}
              </p>
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
             
              {/* {balanceAmount > 0 && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-xs sm:text-sm font-medium"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  Pay Balance
                </button>
              )} */}
              <BookingReceipt
                booking={booking}
                payments={payments}
                vendor={vendor}
                service={service}
                packageData={packageData}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            {/* Event Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Event Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Event Date</p>
                    <p className="text-sm text-black font-medium">{formatDate(booking.event_date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Time</p>
                    <p className="text-sm text-black font-medium">
                      {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Duration</p>
                    <p className="text-sm text-black font-medium">{booking.duration_hours} hours</p>
                  </div>
                </div>
                {booking.guest_count && (
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-600 font-medium mb-0.5">Guest Count</p>
                      <p className="text-sm text-black font-medium">{booking.guest_count} guests</p>
                    </div>
                  </div>
                )}
                {booking.event_location && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <MapPin className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 font-medium mb-0.5">Location</p>
                      <p className="text-sm text-black font-medium">{booking.event_location}</p>
                      {booking.event_address && booking.event_address !== booking.event_location && (
                        <p className="text-xs text-gray-600 mt-0.5">{booking.event_address}</p>
                      )}
                    </div>
                  </div>
                )}
                {booking.event_type && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <FileText className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-600 font-medium mb-0.5">Event Type</p>
                      <p className="text-sm text-black font-medium">{booking.event_type}</p>
                    </div>
                  </div>
                )}
              </div>
              {booking.special_requirements && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t-2 border-gray-200">
                  <p className="text-xs text-gray-600 font-medium mb-1">Special Requirements</p>
                  <p className="text-sm text-black">{booking.special_requirements}</p>
                </div>
              )}
            </div>

            {/* Event Logs */}
            {eventLogs && eventLogs.length > 0 && (
              <EventLogs eventLogs={eventLogs} isLoading={isLoadingEventLogs} />
            )}

            {/* Service & Package Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Service & Package</h3>
              {service && (
                <div className="mb-2 sm:mb-3 pb-2 sm:pb-3 border-b-2 border-gray-200">
                  <p className="text-xs text-gray-600 font-medium mb-0.5">Service</p>
                  <p className="text-sm font-medium text-black">{service.service_name}</p>
                  {service.service_category && (
                    <p className="text-xs text-gray-600 mt-0.5">{service.service_category.category_name}</p>
                  )}
                </div>
              )}
              {packageData && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-0.5">Package</p>
                  <p className="text-sm font-medium text-black mb-1">{packageData.package_name}</p>
                  {packageData.description && (
                    <p className="text-xs sm:text-sm text-gray-600">{packageData.description}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-2 sm:space-y-3">
            {/* Vendor Information */}
            {vendor && (
              <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Vendor Information</h3>
                <div className="space-y-1.5 sm:space-y-2">
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Business Name</p>
                    <p className="text-sm font-medium text-black">{vendor.business_name}</p>
                  </div>
                  {vendor.business_phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-black" />
                      <p className="text-xs sm:text-sm text-black">{vendor.business_phone}</p>
                    </div>
                  )}
                  {vendor.city && vendor.state && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-black" />
                      <p className="text-xs sm:text-sm text-black">{vendor.city}, {vendor.state}</p>
                    </div>
                  )}
                  {vendor.is_verified && (
                    <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-200">
                      <CheckCircle className="w-3.5 h-3.5 text-black" />
                      <p className="text-xs font-medium text-black">Verified Vendor</p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-gray-200 space-y-2">
                  {vendor.business_phone && (
                    <a
                      href={`tel:${vendor.business_phone}`}
                      className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                    >
                      <Phone className="w-4 h-4" />
                      Call Vendor
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowBookingChat(true)}
                    className="relative flex items-center justify-center gap-2 w-full px-3 py-2 border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Message vendor
                    {chatUnreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-semibold leading-none">
                        {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Payment Summary</h3>
              <div className="space-y-1.5 sm:space-y-2">
                {subtotal > 0 && (
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-xs text-gray-600 font-medium">Subtotal:</span>
                    <span className="text-sm font-medium text-black">${formatCurrency(subtotal)}</span>
                  </div>
                )}
                {(parseFloat(booking?.discount_amount) || 0) > 0 && (
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-xs text-gray-600 font-medium">
                      Discount{booking?.discount_code?.code ? ` (${booking.discount_code.code})` : ''}:
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      -${formatCurrency(booking.discount_amount)}
                    </span>
                  </div>
                )}
                {platformFee > 0 && (
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-xs text-gray-600 font-medium">Platform Fee:</span>
                    <span className="text-sm font-medium text-black">
                      ${formatCurrency(platformFee)}
                      {booking.commission_rate && (
                        <span className="text-xs text-gray-500 ml-1">({booking.commission_rate}%)</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center py-1.5 border-b-2 border-gray-200">
                  <span className="text-sm text-black font-bold">Total:</span>
                  <span className="text-base sm:text-lg font-bold text-black">${formatCurrency(totalDue)}</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-xs text-gray-600 font-medium">Total Paid:</span>
                  <span className="text-sm font-medium text-black">${formatCurrency(actualAmountPaid)}</span>
                </div>
                {/* Only show deposit paid if there are actual deposit payments (not when full payment was made) */}
                {showDepositPaid && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs text-gray-600 font-medium">Deposit Paid:</span>
                    <span className="text-sm font-medium text-black">${formatCurrency(depositPaid)}</span>
                  </div>
                )}
                {/* Show full payment amount if full payment was made (but only if not fully paid - otherwise it's just an installment) */}
                {fullPaymentPaid && fullPaymentAmount > 0 && !isFullyPaid && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs text-gray-600 font-medium">Payment Installment:</span>
                    <span className="text-sm font-medium text-black">${formatCurrency(fullPaymentAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600 font-medium">Payment Status:</span>
                  <span className={`text-xs font-medium ${isFullyPaid ? 'text-green-600' : actualAmountPaid > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
                    {isFullyPaid ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Fully Paid
                      </span>
                    ) : actualAmountPaid > 0 ? (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Partial
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" />
                        Unpaid
                      </span>
                    )}
                  </span>
                </div>
                {/* Only show "Full Payment: Paid" when booking is actually fully paid */}
                {isFullyPaid && (
                  <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                    <span className="text-xs text-gray-600 font-medium">Full Payment:</span>
                    <span className="text-xs font-medium text-black flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Paid
                    </span>
                  </div>
                )}
                {balanceAmount > 0 && (
                  <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                    <span className="text-xs text-gray-600 font-medium">Remaining Balance:</span>
                    <span className="text-sm font-medium text-black">
                      ${formatCurrency(balanceAmount)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Payment Actions */}
              <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-gray-200 space-y-2">
                {payments.length > 0 && (
                  <button
                    onClick={() => setShowPaymentHistoryModal(true)}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                  >
                    <CreditCard className="w-4 h-4" />
                    Payment History
                  </button>
                )}
                {balanceAmount > 0 && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                  >
                    <CreditCard className="w-4 h-4" />
                    Pay Balance (${balanceAmount.toFixed(2)})
                  </button>
                )}
                {canReview && (
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                  >
                    <Star className="w-4 h-4" />
                    Leave Review
                  </button>
                )}
                {hasReview && booking.reviews[0] && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= booking.reviews[0].rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-black">
                        {booking.reviews[0].title}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{booking.reviews[0].comment}</p>
                    {!booking.reviews[0].is_approved && (
                      <p className="text-xs text-gray-500 italic">Pending approval</p>
                    )}
                  </div>
                )}
                <BookingReceipt
                  booking={booking}
                  payments={payments}
                  vendor={vendor}
                  service={service}
                  packageData={packageData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <BalancePaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        booking={booking}
        balanceAmount={balanceAmount}
        payments={payments}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        payments={payments}
      />

      {showBookingChat &&
        id &&
        createPortal(
          <div
            className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-chat-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeBookingChat();
            }}
          >
            <div className="w-full max-w-lg max-h-[90vh] flex flex-col">
              <span id="booking-chat-title" className="sr-only">
                Booking chat
              </span>
              <BookingChatPanel bookingId={id} onClose={closeBookingChat} className="max-h-[90vh]" />
            </div>
          </div>,
          document.body
        )}

      {/* Review Modal */}
      {booking && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          bookingId={booking.id}
          onSuccess={handleReviewSuccess}
        />
      )}
    </ClientPageLayout>
  );
};

export default ClientBookingDetails;

